import { GraphQL, GraphQLPagedQueryData } from "@rpi/openapi-api";
import { RpiTableColumns } from "../../rpi-core/table/RpiTable";
import { createEntityColumn } from "../../rpi-core/table/RpiTableHelpers";
import { CellDate } from "../../components/cells/CellDate";
import { CellId } from "../../components/cells/CellId";
import { CellRoute } from "../../components/cells/CellLink";
import { CellOrganization } from "../../components/cells/CellOrganization";
import { OrderStatusCell } from "../../components/cells/OrderStatusCellV2";
import { OrderPaymentStatusCell } from "../../components/cells/OrderPaymentStatusCellV2";
import { getRoutePath } from "../../hooks/useNavigateRoute";
import { formatCurrency } from "../../utils/misc.util";

const orderColumn = createEntityColumn<GraphQL.OrderResponse, GraphQL.OrderSearchField>();

const baseOrderColumnDefs = {
  orderDate: orderColumn<"createdAt">({
    header: "Order Date",
    accessor: "createdAt",
    cell: ({ row }) => <CellDate date={row.createdAt} format="MM/DD/YYYY" />,
    sortKey: GraphQL.OrderSearchField.CreatedAt,
    size: 110,
  }),
  orderPublicId: orderColumn<"orderPublicId" | "customerOrderId" | "customer">({
    header: "Order ID",
    accessor: "orderPublicId",
    cell: ({ row }) => {
      const customerPublicId = row.customer?.publicId;
      const { orderPublicId, customerOrderId } = row;
      if (!customerPublicId || !customerOrderId) return orderPublicId || customerOrderId || "—";
      if (!orderPublicId) {
        return <CellRoute route={["order", { customerPublicId, customerOrderId }]}>{customerOrderId}</CellRoute>;
      }
      return (
        <CellId value={orderPublicId} to={getRoutePath("order", { customerPublicId, customerOrderId })} truncateStart />
      );
    },
    size: 260,
    minSize: 140,
  }),
  customerOrderId: orderColumn<"customerOrderId" | "orderPublicId">({
    header: "Customer Order ID",
    accessor: "customerOrderId",
    cell: ({ row }) => {
      const customerOrderId = row.customerOrderId?.trim();
      if (!customerOrderId || customerOrderId === row.orderPublicId) return "—";
      return <CellId value={customerOrderId} truncateStart />;
    },
    size: 190,
    minSize: 140,
  }),
  organization: orderColumn<"customer">({
    header: "Organization",
    accessor: "customer",
    cell: ({ row }) => <CellOrganization customer={row.customer} />,
    size: 120,
  }),
  orderStatus: orderColumn<"publicOrderStatus">({
    header: "Order Status",
    accessor: "publicOrderStatus",
    cell: ({ row }) => <OrderStatusCell status={row.publicOrderStatus} />,
    size: 170,
  }),
  orderStatusMessage: orderColumn<"lastStatusMessage">({
    header: "Status Message",
    accessor: "lastStatusMessage",
    cell: ({ row }) => row.lastStatusMessage?.trim() || "—",
    size: 150,
  }),
  orderStatusUpdatedDate: orderColumn<"updatedAt">({
    header: "Status Updated Date",
    accessor: "updatedAt",
    cell: ({ row }) => <CellDate date={row.updatedAt} format="MM/DD/YYYY" />,
    size: 150,
    sortKey: GraphQL.OrderSearchField.UpdatedAt,
  }),
  orderPaymentSubmittedDate: orderColumn<"submittedAt">({
    header: "Submitted Date",
    accessor: "submittedAt",
    cell: ({ row }) => (row.submittedAt ? <CellDate date={row.submittedAt} format="MM/DD/YYYY" /> : "-"),
    size: 130,
    sortKey: GraphQL.OrderSearchField.SubmittedAt,
  }),
  orderPaymentAmount: orderColumn<"pricing" | "currency">({
    header: "Amount",
    accessor: "pricing",
    cell: ({ row }) => formatCurrency(row.currency, row.pricing?.orderTotal || 0),
    size: 90,
    minSize: 82,
  }),

  orderPaymentStatus: orderColumn<"paymentStatus">({
    header: "Payment Status",
    accessor: "paymentStatus",
    cell: ({ row }) => <OrderPaymentStatusCell status={row.paymentStatus} />,
    size: 100,
    minSize: 90,
  }),
};

export const orderColumns: RpiTableColumns<GraphQLPagedQueryData<GraphQL.OrdersQuery>> = (user) => [
  baseOrderColumnDefs.orderDate,
  baseOrderColumnDefs.orderPublicId,
  baseOrderColumnDefs.customerOrderId,
  user.is.SUPER_ADMIN && baseOrderColumnDefs.organization,
  baseOrderColumnDefs.orderStatus,
  baseOrderColumnDefs.orderStatusMessage,
  baseOrderColumnDefs.orderStatusUpdatedDate,
  !user.isSandbox && user.is.SUPER_ADMIN && baseOrderColumnDefs.orderPaymentSubmittedDate,
  !user.isSandbox && baseOrderColumnDefs.orderPaymentAmount,
  !user.isSandbox && baseOrderColumnDefs.orderPaymentStatus,
];
